/**
 * OverlayOrderContext - Temporal ordering for sibling overlays.
 *
 * Modals and slideouts are independent overlay systems that can be open
 * simultaneously. Without coordination, their z-indexes are static —
 * one type always renders above the other regardless of which opened first.
 *
 * This context assigns stacking levels based on **open order**: each overlay
 * that opens gets a higher level than all previously opened overlays,
 * ensuring the most recently opened overlay renders on top.
 *
 * @example
 * ```tsx
 * // In Root.tsx — wrap all overlay providers
 * <OverlayOrderProvider>
 *   <SlideoutStackProvider>
 *     <ModalStackProvider>
 *       ...
 *     </ModalStackProvider>
 *   </SlideoutStackProvider>
 * </OverlayOrderProvider>
 *
 * // In an overlay component
 * const overlayOrder = useOverlayOrder(isOpen);
 * // Wrap children in StackingLevelProvider so useStackingZIndex() works
 * <StackingLevelProvider level={overlayOrder}>
 *   <OverlayContent />
 * </StackingLevelProvider>
 * ```
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */

interface OverlayOrderContextValue {
  /** Register an overlay. Returns the assigned stacking level. */
  register: () => number;
}

const OverlayOrderContext = createContext<OverlayOrderContextValue | null>(
  null
);

export function OverlayOrderProvider({ children }: { children: ReactNode }) {
  const counterRef = useRef(0);

  const register = useCallback(() => {
    counterRef.current += 1;
    // Each tier gets 2 stacking levels of headroom (200 z-index units).
    // This ensures StackingLayer children within an overlay (level + 1)
    // stay below the next overlay tier (level + 2).
    return counterRef.current * 2;
  }, []);

  const value = useMemo(() => ({ register }), [register]);

  return (
    <OverlayOrderContext.Provider value={value}>
      {children}
    </OverlayOrderContext.Provider>
  );
}

/**
 * Hook for overlays to get an order-based stacking level.
 *
 * When the key signals "active", registers with the shared counter and
 * returns a stacking level guaranteed to be higher than all previously
 * registered overlays. Use this level with `<StackingLevelProvider>` so
 * that `useStackingZIndex()` inside the overlay computes the correct z-index.
 *
 * The `activeKey` doubles as a re-registration trigger: when its value
 * changes (e.g. a slideout swaps from entity A to entity B without
 * unmounting), the effect re-runs and the overlay registers again with a
 * fresh, higher level. Pass the entity/version id directly so each new
 * "session" bumps the stacking level above any modals/overlays opened in
 * the meantime.
 *
 * Returns 0 when inactive or when no provider is present (safe for tests).
 *
 * @param activeKey - Numeric key (>0 = active) or string key (non-empty = active)
 * @returns Stacking level (0 when inactive, positive when active)
 */
export function useOverlayOrder(activeKey: number | string): number {
  const ctx = useContext(OverlayOrderContext);
  const [level, setLevel] = useState(0);

  useEffect(() => {
    const isActive =
      typeof activeKey === 'string' ? activeKey.length > 0 : activeKey > 0;
    if (isActive && ctx) {
      const assigned = ctx.register();
      setLevel(assigned);
      return () => {
        setLevel(0);
      };
    }
  }, [activeKey, ctx]);

  return level;
}
